import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, BellRing, BellOff, Volume2, VolumeX, Waves, Moon } from "lucide-react";
import { useNotifications } from "@/context/NotificationsContext";

const RELATIVE = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

/**
 * Presentational bell — all polling / dedupe / toast logic lives in
 * NotificationsProvider so multiple bell mounts (desktop + mobile top-bar)
 * share one poll and one state.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const {
    items, unread, pulse,
    markAllRead, openNotif,
    nativeSupported, nativeEnabled, nativePermission,
    requestNative, disableNative,
    soundMode, setSoundMode,
    quietHours, setQuietHours, isQuietNow,
  } = useNotifications();

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleOpen = async (n) => {
    setOpen(false);
    await openNotif(n);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label="Notifications"
        className={`relative h-10 w-10 grid place-items-center border border-[#262626] bg-[#0A0A0A] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors text-white ${pulse ? "notif-bell-pulse" : ""}`}
      >
        <Bell className={`w-4 h-4 ${pulse ? "text-[#CCFF00]" : ""}`} strokeWidth={1.75} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#CCFF00] text-black text-[10px] font-bold grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 max-w-[92vw] bg-[#0A0A0A] border border-[#262626] shadow-lg z-50"
          style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.6)" }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626]">
            <div className="label-mono">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1"
                data-testid="notification-mark-all"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Native desktop-alert toggle. Hidden on unsupported browsers so we
              don't dangle a broken CTA in Safari's private-mode etc. */}
          {nativeSupported && (
            <div
              className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#141414] bg-[#141414]/60"
              data-testid="native-notifications-row"
            >
              <div className="flex items-center gap-2 min-w-0">
                {nativeEnabled
                  ? <BellRing className="w-3.5 h-3.5 text-[#CCFF00] shrink-0" strokeWidth={2} />
                  : <BellOff className="w-3.5 h-3.5 text-[#525252] shrink-0" strokeWidth={2} />}
                <div className="min-w-0">
                  <div className="text-[11px] text-white leading-tight">Desktop alerts</div>
                  <div className="text-[10px] text-[#525252] leading-tight">
                    {nativeEnabled
                      ? "On — you'll get an OS notification when the tab isn't in focus."
                      : nativePermission === "denied"
                        ? "Blocked in your browser settings."
                        : "Off — enable to get alerts even when this tab is in the background."}
                  </div>
                </div>
              </div>
              {nativeEnabled ? (
                <button
                  onClick={disableNative}
                  className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white shrink-0"
                  data-testid="native-notifications-disable"
                >
                  Turn off
                </button>
              ) : (
                <button
                  onClick={requestNative}
                  disabled={nativePermission === "denied"}
                  className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:text-white shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="native-notifications-enable"
                >
                  Enable
                </button>
              )}
            </div>
          )}

          {/* Iter146 — Sound-mode picker (Silent / Chirp / Whistle). Clicking a
              tile persists the choice AND previews the sound so the user
              hears what they're getting. */}
          <div
            className="px-3 py-2 border-b border-[#141414] bg-[#141414]/60"
            data-testid="notification-sound-row"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] text-white leading-tight">Sound</div>
              <div className="text-[10px] text-[#525252] leading-tight">Tap to preview</div>
            </div>
            <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Notification sound">
              {[
                { k: "silent",  l: "Silent",  Icon: VolumeX,  c: "#A3A3A3" },
                { k: "chirp",   l: "Chirp",   Icon: Volume2,  c: "#CCFF00" },
                { k: "whistle", l: "Whistle", Icon: Waves,    c: "#FF9F1C" },
              ].map(({ k, l, Icon, c }) => {
                const on = soundMode === k;
                return (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setSoundMode(k)}
                    data-testid={`notif-sound-${k}`}
                    className="py-1.5 px-1 text-[10px] uppercase tracking-wider inline-flex items-center justify-center gap-1 border transition-colors"
                    style={on
                      ? { background: c, color: "#0A0A0A", borderColor: c, fontWeight: 700 }
                      : { borderColor: "#262626", color: "#A3A3A3" }}
                  >
                    <Icon className="w-3 h-3" strokeWidth={2} />
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Iter147 — Quiet hours window. Set start + end (24h HH:MM) to
              mute the audible ping between those times. Bell visuals + desktop
              alerts (if enabled) still fire so nothing is missed. */}
          <div
            className="px-3 py-2 border-b border-[#141414] bg-[#141414]/60"
            data-testid="quiet-hours-row"
          >
            <div className="flex items-center justify-between mb-1.5 gap-2">
              <div className="flex items-center gap-1.5">
                <Moon className={`w-3.5 h-3.5 ${isQuietNow() ? "text-[#66C2FF]" : "text-[#525252]"}`} strokeWidth={2} />
                <div className="text-[11px] text-white leading-tight">Quiet hours</div>
              </div>
              {(quietHours.start || quietHours.end) && (
                <button
                  type="button"
                  onClick={() => setQuietHours("", "")}
                  data-testid="quiet-hours-clear"
                  className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">
                From
                <input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => setQuietHours(e.target.value, quietHours.end)}
                  data-testid="quiet-hours-start"
                  className="mt-0.5 w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs text-white focus:outline-none focus:border-[#66C2FF]"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">
                To
                <input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => setQuietHours(quietHours.start, e.target.value)}
                  data-testid="quiet-hours-end"
                  className="mt-0.5 w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 text-xs text-white focus:outline-none focus:border-[#66C2FF]"
                />
              </label>
            </div>
            <div className="text-[10px] text-[#525252] mt-1 leading-tight">
              {isQuietNow()
                ? "Quiet now — sound muted, bell still updates."
                : (quietHours.start && quietHours.end)
                  ? `Muted between ${quietHours.start} and ${quietHours.end}.`
                  : "Set a start and end (24h) to mute the sound overnight."}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {!items.length && (
              <div className="p-4 text-xs text-[#A3A3A3]" data-testid="notification-empty">
                No notifications yet. When someone quotes you, approves a claim, or emails you a plan, it&apos;ll show up here.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleOpen(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-[#141414] hover:bg-[#141414] transition-colors ${n.is_read ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#CCFF00] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-semibold line-clamp-2">{n.title}</div>
                    {n.body && <div className="text-[11px] text-[#A3A3A3] line-clamp-2 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-[#737373] mt-1 label-mono">{RELATIVE(n.created_at)} · {n.created_at ? new Date(n.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            data-testid="notification-view-all"
            className="block px-3 py-2 border-t border-[#262626] text-center text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00]"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
